import { Scope } from '@myre/domain-i18n-core';
import {
  authReducer,
  getMetaReducers,
  provideMyre,
  provideNotificationState,
  type NotificationsState,
} from '@myre/ui-domain-core';
import { DateFormatPipe, NumberFormatPipe } from '@myre/ui-primitive-core';
import { provideMyreFormsConfig } from '@myre/ui-primitive-forms';
import { provideLayoutsConfig } from '@myre/ui-primitive-layouts';
import { provideNavbarConfig } from '@myre/ui-primitive-navbar';

import { DatePipe, DecimalPipe, PercentPipe } from '@angular/common';
import {
  provideHttpClient,
  withInterceptorsFromDi,
} from '@angular/common/http';
import {
  makeEnvironmentProviders,
  type EnvironmentProviders,
} from '@angular/core';
import { provideAnimations } from '@angular/platform-browser/animations';
import { provideEffects } from '@ngrx/effects';
import {
  provideRouterStore,
  routerReducer,
  type RouterReducerState,
} from '@ngrx/router-store';
import { provideStore, type Action, type ActionReducerMap } from '@ngrx/store';
import { provideStoreDevtools } from '@ngrx/store-devtools';
import { environment } from 'environments/environment';

import { integrationReducer } from '../admin/integration/store/integration.reducer';
import { type IntegrationState } from '../admin/integration/store/interfaces';
import { AuthService } from '../guards/auth.service';

import { CoreEffects } from './store/core.effects';
import { dataReducer } from './store/data.reducer';
import { type AuthState, type DataState } from './store/interfaces';

export interface RootState {
  auth: AuthState;
  data: DataState;
  integration: IntegrationState;
  notifications: NotificationsState;
  router: RouterReducerState;
}

export const reducers: ActionReducerMap<RootState> = {
  auth: authReducer,
  data: dataReducer,
  notifications: null,
  router: routerReducer,
  integration: integrationReducer,
};

export function provideCoreConfig(): EnvironmentProviders {
  return makeEnvironmentProviders([
    provideHttpClient(withInterceptorsFromDi()),
    DateFormatPipe,
    DatePipe,
    DecimalPipe,
    NumberFormatPipe,
    PercentPipe,
    provideMyre({
      i18nScope: Scope.User,
      authService: AuthService,
    }),
    provideAnimations(),
    provideStore(reducers, {
      metaReducers: getMetaReducers(environment.production),
      runtimeChecks: {
        strictActionImmutability: false,
      },
    }),
    provideRouterStore({
      stateKey: 'router',
    }),
    provideEffects(CoreEffects),
    ...(environment.production
      ? []
      : [
          provideStoreDevtools({
            actionSanitizer(a: Action): Action {
              return { ...a, type: a.type };
            },
            connectInZone: true,
          }),
        ]),
    provideNotificationState(),
    provideLayoutsConfig(),
    provideMyreFormsConfig(),
    provideNavbarConfig(),
  ]);
}
